import { TemplateVariableReferenceField } from "./template-variable-reference-field";
import {
  getStepVariableBindingReadiness,
  type StepVariableSourceGroup,
} from "@/domains/templates/step-variable-bindings";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import type { Variable } from "@/domains/templates/types";
import type { BindingDraft } from "../lib/subanalysis-draft";
import { formatVariableDefault } from "../lib/format-variable-default";

type Props = {
  row: BindingDraft;
  sourceGroups?: StepVariableSourceGroup[];
  ordinary?: boolean;
  parentVariables: Variable[];
  onChange: (row: BindingDraft) => void;
  disabled: boolean;
  errors?: Record<string, string>;
};
const modes = {
  unbound: "Unbound (incomplete)",
  step_variable: "Earlier-section variable",
  item: "Item field",
  parent_variable: "Parent variable",
  constant: "Fixed value",
  target_default: "Use child default",
} as const;

export function SubanalysisBindingRow({
  row,
  sourceGroups = [],
  ordinary = false,
  parentVariables,
  onChange,
  disabled,
  errors = {},
}: Props) {
  const label = row.target.variable_name_frontend ?? row.target.variable_name;
  const id = `binding-${row.target.variable_id}`;
  const update = (change: Partial<BindingDraft>) =>
    onChange({ ...row, ...change });
  const fieldError = (field: string) =>
    errors[field] ? (
      <p role="alert" className="text-destructive text-sm">
        {errors[field]}
      </p>
    ) : null;
  const options = Array.isArray(row.target.variable_options)
    ? row.target.variable_options.flatMap((option) => {
        if (typeof option === "string")
          return [{ value: option, label: option }];
        if (!option || typeof option !== "object" || Array.isArray(option))
          return [];
        const value = option.value ?? option.key ?? option.id;
        return typeof value === "string"
          ? [
              {
                value,
                label: typeof option.label === "string" ? option.label : value,
              },
            ]
          : [];
      })
    : [];
  return (
    <fieldset className="space-y-3 rounded-md border p-3" disabled={disabled}>
      <legend className="px-1 font-medium">{label}</legend>
      <p className="text-muted-foreground text-sm">
        {row.target.variable_type} ·{" "}
        {row.target.variable_is_required ? "Required" : "Optional"}
      </p>
      <Select
        value={row.kind}
        onValueChange={(value) =>
          update({ kind: value as BindingDraft["kind"] })
        }
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={`${label} source`}
          aria-invalid={!!errors.binding_kind}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(modes)
            .filter(
              ([value]) =>
                !ordinary ||
                ["constant", "step_variable", "unbound"].includes(value),
            )
            .map(([value, name]) => (
              <SelectItem key={value} value={value}>
                {name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      {fieldError("binding_kind")}
      {fieldError("binding_target_variable_id")}
      {row.kind === "unbound" && (
        <p role="status">Incomplete: choose a source or value.</p>
      )}
      {row.kind === "step_variable" && (
        <div className="space-y-2">
          <TemplateVariableReferenceField
            label="Source variable"
            value={row.sourceVariableId}
            disabled={disabled}
            options={sourceGroups.flatMap((group) =>
              [...group.inputs, ...group.outputs].map((source) => ({
                id: source.id,
                name: `${group.stepName} → ${source.direction === "input" ? "Input" : "Output"} → ${source.variable.variable_name_frontend || source.variable.variable_name} (${source.type})`,
              })),
            )}
            onChange={(sourceVariableId) =>
              update({ sourceVariableId, path: "" })
            }
          />
          {fieldError("binding_source_variable_id")}
          <Label htmlFor={`${id}-projection`}>
            Optional projection (JSON Pointer)
          </Label>
          <Input
            id={`${id}-projection`}
            value={row.path}
            onChange={(event) => update({ path: event.target.value })}
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">
            Leave empty to reference the whole value.
          </p>
          {fieldError("binding_path")}
          {(() => {
            const source = sourceGroups
              .flatMap((group) => [...group.inputs, ...group.outputs])
              .find((item) => item.id === row.sourceVariableId);
            return source &&
              getStepVariableBindingReadiness(
                source,
                row.path,
                row.target.variable_type,
              ) === "incomplete" ? (
              <p role="status">
                Incomplete: this source or projection has no verified value
                type. Declare a typed source output.
              </p>
            ) : null;
          })()}
        </div>
      )}
      {row.kind === "item" && (
        <div className="space-y-1">
          <Label htmlFor={`${id}-path`}>Item JSON Pointer</Label>
          <Input
            id={`${id}-path`}
            aria-label={`${label} item path`}
            aria-invalid={!!errors.binding_path}
            value={row.path}
            onChange={(event) => update({ path: event.target.value })}
            disabled={disabled}
            placeholder="/name"
          />
          <p className="text-muted-foreground text-xs">
            An empty path selects the whole item.
          </p>
          {fieldError("binding_path")}
        </div>
      )}
      {row.kind === "parent_variable" && (
        <div className="space-y-1">
          <Select
            value={row.sourceVariableId}
            onValueChange={(sourceVariableId) => update({ sourceVariableId })}
            disabled={disabled}
          >
            <SelectTrigger
              aria-label={`${label} parent variable`}
              aria-invalid={!!errors.binding_source_variable_id}
            >
              <SelectValue placeholder="Select parent variable" />
            </SelectTrigger>
            <SelectContent>
              {parentVariables.map((variable) => (
                <SelectItem
                  key={variable.variable_id}
                  value={variable.variable_id}
                >
                  {variable.variable_name_frontend ?? variable.variable_name}
                  {variable.variable_default !== null
                    ? ` — default ${formatVariableDefault(variable.variable_default)}`
                    : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldError("binding_source_variable_id")}
        </div>
      )}
      {row.kind === "constant" && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={row.constantIsNull}
              onCheckedChange={(checked) =>
                update({
                  constantIsNull: checked === true,
                  constantPresent: true,
                })
              }
              disabled={disabled}
            />
            Explicit null
          </label>
          {!row.constantIsNull &&
            (row.target.variable_type === "boolean" ? (
              <Select
                value={row.constantPresent ? row.constantText : ""}
                onValueChange={(constantText) =>
                  update({ constantText, constantPresent: true })
                }
                disabled={disabled}
              >
                <SelectTrigger
                  aria-label={`${label} fixed value`}
                  aria-invalid={!!errors.variable_default}
                >
                  <SelectValue placeholder="Choose true or false" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">True</SelectItem>
                  <SelectItem value="false">False</SelectItem>
                </SelectContent>
              </Select>
            ) : row.target.variable_type === "enum" && options.length ? (
              <Select
                value={row.constantText}
                onValueChange={(constantText) =>
                  update({ constantText, constantPresent: true })
                }
                disabled={disabled}
              >
                <SelectTrigger
                  aria-label={`${label} fixed value`}
                  aria-invalid={!!errors.variable_default}
                >
                  <SelectValue placeholder="Choose a value" />
                </SelectTrigger>
                <SelectContent>
                  {options
                    .filter((option) => option.value !== "")
                    .map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            ) : ["text", "json", "array"].includes(row.target.variable_type) ? (
              <Textarea
                aria-label={`${label} fixed value`}
                aria-invalid={!!errors.variable_default}
                value={row.constantText}
                onChange={(event) =>
                  update({
                    constantText: event.target.value,
                    constantPresent: true,
                  })
                }
                disabled={disabled}
                placeholder={
                  row.target.variable_type === "array" ? '["value"]' : undefined
                }
              />
            ) : (
              <Input
                aria-label={`${label} fixed value`}
                aria-invalid={!!errors.variable_default}
                type={
                  ["number", "integer"].includes(row.target.variable_type)
                    ? "number"
                    : row.target.variable_type === "date"
                      ? "date"
                      : row.target.variable_type === "datetime"
                        ? "datetime-local"
                        : "text"
                }
                step={row.target.variable_type === "integer" ? "1" : "any"}
                value={row.constantText}
                onChange={(event) =>
                  update({
                    constantText: event.target.value,
                    constantPresent: true,
                  })
                }
                disabled={disabled}
              />
            ))}
          {row.target.variable_type === "enum" &&
            options.some((option) => option.value === "") && (
              <button
                type="button"
                className="text-sm underline"
                onClick={() =>
                  update({ constantText: "", constantPresent: true })
                }
                disabled={disabled}
              >
                Use empty value
              </button>
            )}
          {fieldError("variable_default")}
          {!row.constantPresent && (
            <p className="text-muted-foreground text-xs">
              Enter a value or choose explicit null.
              {["string", "text"].includes(row.target.variable_type) && (
                <button
                  type="button"
                  className="ml-1 underline"
                  onClick={() =>
                    update({ constantText: "", constantPresent: true })
                  }
                  disabled={disabled}
                >
                  Use empty string
                </button>
              )}
            </p>
          )}
        </div>
      )}
      {row.kind === "target_default" && (
        <p className="text-muted-foreground text-sm">
          {row.target.variable_default !== null
            ? `Child default: ${formatVariableDefault(row.target.variable_default)}`
            : row.target.variable_is_required
              ? "No definition default. An inherited default must be available when saving."
              : "No child default; this optional input will be omitted."}
        </p>
      )}
    </fieldset>
  );
}
